import { z } from 'zod'

/**
 * The view state Product / Price Sync reads from the URL: which section is
 * open (OTTO price upload or Shopify sync), which store is picked, which of
 * its runs is open, which page of runs is shown and the SKU search over the
 * open run's outcomes — and, for Shopify, which sync's runs are listed
 * (products or orders) — so a link to "Venta US, its orders syncs" opens
 * exactly that.
 *
 * Every field optional and `.catch(undefined)` — see CLAUDE.md §4.5
 * for why both. The sheet being uploaded, the dry-run box and a sync's date
 * range are a form draft, not view state, and stay out of the URL.
 */
export const productPriceSyncSearchSchema = z.object({
  tab: z
    .enum(['prices', 'shopify'])
    .optional()
    .catch(undefined)
    .describe('OTTO price upload (prices) or Shopify sync. Default prices.'),
  store: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The OTTO or Shopify store picked, by id.'),
  run: z.string().min(1).optional().catch(undefined).describe('The run open in detail, by id.'),
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('Page of the runs list, from 1. Default 1.'),
  sku: z
    .string()
    .max(80)
    .optional()
    .catch(undefined)
    .describe('SKU search over the open price run’s outcomes.'),
  kind: z
    .enum(['products', 'orders'])
    .optional()
    .catch(undefined)
    .describe('Shopify: which sync’s runs are listed, products or orders. Default products.'),
})

export type ProductPriceSyncSearch = z.infer<typeof productPriceSyncSearchSchema>

export const PRODUCT_PRICE_SYNC_DEFAULTS = { page: 1, tab: 'prices', kind: 'products' } as const

/** Drop any value that equals its default, so the URL carries only what differs. */
export function withoutPriceSyncDefaults(search: ProductPriceSyncSearch): ProductPriceSyncSearch {
  const pruned: ProductPriceSyncSearch = {}
  if (search.tab !== undefined && search.tab !== PRODUCT_PRICE_SYNC_DEFAULTS.tab) {
    pruned.tab = search.tab
  }
  if (search.store !== undefined) pruned.store = search.store
  if (search.run !== undefined) pruned.run = search.run
  if (search.sku) pruned.sku = search.sku
  if (search.page !== undefined && search.page !== PRODUCT_PRICE_SYNC_DEFAULTS.page) {
    pruned.page = search.page
  }
  if (search.kind !== undefined && search.kind !== PRODUCT_PRICE_SYNC_DEFAULTS.kind) {
    pruned.kind = search.kind
  }
  return pruned
}
